import { asList, isBlank } from './fields';
import type { BatchRow, CurationRow, RowValues } from './types';

/** One row as the map needs it, before or after creation. */
export interface MapRow {
  key: string;
  surveyArea: string | null;
  /** The resolved survey area; null when it is blank or not resolved. */
  referenceLocationId: number | null;
  quarterCalls: string[];
  /** Lots or Grid Exception: no computed shape; GIS makes it. */
  gisShape: boolean;
  invalid: boolean;
  /** The created asset (Salesforce tract id), after creation. */
  assetId: string | null;
}

export const GIS_SHAPE_NOTE =
  'Because Lots and/or Grid Exception is set, GIS will be notified to make the shape. The map shows the whole survey area.';

export const hasGisShape = (values: RowValues) =>
  !isBlank(values.lots) || values.gridException === true;

/**
 * The survey area `values` resolve to: the server's `id` while the survey area
 * is still the one it resolved (`resolvedFor`), else an id picked in the form.
 * A survey area typed but never resolved has none.
 * @param {RowValues} values - The row's current values
 * @param {RowValues | undefined} resolvedFor - The values `id` was resolved for
 * @param {number | null | undefined} id - The server's reference location id
 * @return {number | null} The reference location id
 */
export const resolvedLocationId = (
  values: RowValues,
  resolvedFor: RowValues | undefined,
  id: number | null | undefined
) => {
  if (isBlank(values.surveyArea) && isBlank(values.surveyAreaId)) return null;
  if (
    resolvedFor &&
    values.surveyArea === resolvedFor.surveyArea &&
    (values.surveyAreaId ?? null) === (resolvedFor.surveyAreaId ?? null)
  ) {
    return id ?? null;
  }
  return typeof values.surveyAreaId === 'number' ? values.surveyAreaId : null;
};

export const mapRow = (
  key: string,
  values: RowValues,
  referenceLocationId: number | null,
  { invalid = false, assetId = null as string | null } = {}
): MapRow => ({
  key,
  surveyArea: isBlank(values.surveyArea) ? null : String(values.surveyArea),
  referenceLocationId,
  quarterCalls: asList(values.quarterCalls),
  gisShape: hasGisShape(values),
  invalid,
  assetId,
});

export const curationMapRow = (row: CurationRow) =>
  mapRow(
    row.clientId,
    row.values,
    resolvedLocationId(
      row.values,
      row.result?.values,
      row.result?.referenceLocationId
    ),
    { invalid: !row.pending && !row.result?.valid }
  );

/**
 * @param {BatchRow} row - A submitted row
 * @param {RowValues} values - Its values as shown (a pending edit, else its payload)
 * @return {MapRow} The row for the map
 */
export const batchMapRow = (row: BatchRow, values: RowValues) =>
  mapRow(
    String(row.id),
    values,
    resolvedLocationId(values, row.payload, row.referenceLocationId),
    { assetId: row.asset?.id ?? null }
  );

/**
 * Why View asset on map is off for a row, or null when it is on.
 * @param {MapRow} row - The row
 * @return {string | null} The reason
 */
export const mapBlocker = (row: MapRow) => {
  // A survey area resolved from its ID alone has no name, and still maps.
  if (row.referenceLocationId !== null) return null;
  return row.surveyArea === null
    ? 'Enter a survey area to view the map'
    : 'Resolve the survey area to view the map';
};
